import { useCallback } from 'react'
import { useTranslation, interpolate } from '../i18n'
import { ApiError } from '../types/apiError'

/**
 * Resolve o texto que o usuário vê para um ApiError.
 *
 * Regra (invariante 8 do CLAUDE.md — toda string visível vem do dicionário):
 *
 * - `usedFallback === false` → a mensagem veio da API. O middleware repassa o
 *   Accept-Language do navegador, então ela já chega no idioma pedido; exibir
 *   direto é o comportamento correto e mais informativo.
 * - `usedFallback === true` → a API não mandou mensagem utilizável. Aí o texto
 *   sai do dicionário local, nunca da tabela em português do normalizador.
 */
export function useApiErrorMessage() {
  const { t } = useTranslation()

  return useCallback(
    (erro: ApiError, fallback?: string): string => {
      if (!erro.usedFallback) return erro.message

      if (erro.code === 'NETWORK') return t.errors.noConnection

      const porStatus = t.errors.byStatus as Record<string, string | undefined>

      return porStatus[String(erro.status)] ?? fallback ?? t.errors.generic
    },
    [t]
  )
}

/** Texto de espera do 429, quando o middleware repassou o Retry-After. */
export function useRetryAfterMessage() {
  const { t } = useTranslation()

  return useCallback(
    (erro: ApiError): string | null =>
      erro.retryAfter === undefined
        ? null
        : interpolate(t.errors.retryAfter, { segundos: erro.retryAfter }),
    [t]
  )
}
